import { computed, shallowRef } from 'vue';
import { assetUrl } from './directus';
import type { Category, DirectusCategory, LocationType } from './types';

import academicPin from '@/assets/pins/building/academic-center.png';
import administrationPin from '@/assets/pins/building/administration.png';
import athleticPin from '@/assets/pins/building/sports.png';
import diningPin from '@/assets/pins/building/dining.png';
import eventPin from '@/assets/pins/building/event-space.png';
import medicalPin from '@/assets/pins/building/medical.png';
import residencePin from '@/assets/pins/building/residence.png';
import visitorPin from '@/assets/pins/parking/visitor.png';
import reservedPin from '@/assets/pins/parking/reserved.png';
import generalPin from '@/assets/pins/parking/general.png';
import residentialPin from '@/assets/pins/parking/residential.png';
import accessiblePin from '@/assets/pins/parking/accessible.png';

import capIcon from '@/assets/ui/icons/graduation-cap.svg';
import usersIcon from '@/assets/ui/icons/users.svg';
import ballIcon from '@/assets/ui/icons/tennis-ball.svg';
import forkIcon from '@/assets/ui/icons/fork-knife.svg';
import masksIcon from '@/assets/ui/icons/masks-theater.svg';
import hospitalIcon from '@/assets/ui/icons/hospital.svg';
import houseIcon from '@/assets/ui/icons/house-chimney-window.svg';
import carsIcon from '@/assets/ui/icons/cars.svg';
import reservedIcon from '@/assets/ui/icons/reserved.svg';
import squareParkingIcon from '@/assets/ui/icons/square-parking.svg';
import garageIcon from '@/assets/ui/icons/garage-car.svg';
import accessibleIcon from '@/assets/ui/icons/accessible-icon-brands.svg';

// Built-in list, used until (or unless) the Directus `categories` collection loads, and as the
// fallback pin/icon for a Directus category without its own files.
// Keys match the building_type / parking_type choices on Directus `locations`.
// Categories with no locations are hidden in the UI, so listing one here is harmless.
const builtIn: Category[] = [
  { key: 'academic-center', type: 'building', label: 'Academic Centers', pinUrl: academicPin, iconUrl: capIcon },
  { key: 'administration', type: 'building', label: 'Administration', pinUrl: administrationPin, iconUrl: usersIcon },
  { key: 'athletic-center', type: 'building', label: 'Athletics', pinUrl: athleticPin, iconUrl: ballIcon },
  { key: 'dining', type: 'building', label: 'Dining', pinUrl: diningPin, iconUrl: forkIcon },
  { key: 'event-space', type: 'building', label: 'Event Spaces', pinUrl: eventPin, iconUrl: masksIcon },
  { key: 'medical-facility', type: 'building', label: 'Medical Facilities', pinUrl: medicalPin, iconUrl: hospitalIcon },
  { key: 'residence-hall', type: 'building', label: 'Residence Halls', pinUrl: residencePin, iconUrl: houseIcon },
  { key: 'visitor', type: 'parking', label: 'Visitor Parking', pinUrl: visitorPin, iconUrl: carsIcon },
  { key: 'general', type: 'parking', label: 'General Parking', pinUrl: generalPin, iconUrl: squareParkingIcon },
  { key: 'reserved', type: 'parking', label: 'Reserved Parking', pinUrl: reservedPin, iconUrl: reservedIcon },
  { key: 'residential', type: 'parking', label: 'Residential Parking', pinUrl: residentialPin, iconUrl: garageIcon },
  { key: 'accessible', type: 'parking', label: 'Accessible Parking', pinUrl: accessiblePin, iconUrl: accessibleIcon },
];

const builtInByKey = new Map(builtIn.map((c) => [c.key, c]));
const fallback = { building: builtInByKey.get('academic-center')!, parking: builtInByKey.get('general')! };

/** Reactive list: templates, computeds and the map pins update when Directus data arrives. */
export const categories = shallowRef<Category[]>(builtIn);

const byKey = computed(() => new Map(categories.value.map((c) => [c.key, c])));

/** Replace the built-in list with Directus rows (order, labels, pins, icons). */
export function setCategories(rows: DirectusCategory[]): void {
  const list: Category[] = [];
  for (const row of rows) {
    if (!row.key || !row.label) continue;
    const type: LocationType = row.type === 'parking' ? 'parking' : 'building';
    const known = builtInByKey.get(row.key) ?? fallback[type];
    list.push({
      key: row.key,
      type,
      label: row.label,
      pinUrl: row.pin ? assetUrl(row.pin) : known.pinUrl,
      iconUrl: row.icon ? assetUrl(row.icon) : known.iconUrl,
    });
  }
  if (list.length) categories.value = list;
}

export function categoryByKey(key: string | null | undefined): Category | undefined {
  return key ? byKey.value.get(key) : undefined;
}

export function categoriesOfType(type: LocationType): Category[] {
  return categories.value.filter((c) => c.type === type);
}
